import * as THREE from "three";
import { configure } from "./skin";
import type { GarmentDims } from "./character";

/**
 * Layered clothing. Garments stack over the bare base skin, innermost → outermost:
 *
 *   base skin (no clothing) → underwear → inner (shirts/pants) → outer (jackets/
 *   hoodies) → hat (accessories)
 *
 * Every garment renders as protruding voxel shells — the same construction as the
 * classic skin-overlay layer — but each layer tops out at a FRACTION of one skin
 * texel, chosen so a full stack (jacket on) reaches exactly the familiar 1-texel
 * silhouette. Hats may extrude beyond it per texel (`extrude`): a top hat is just
 * head-top texels with a tall extrude.
 *
 * A garment texture is a standard 64×64 Minecraft-skin-layout PNG whose OPAQUE
 * texels (alpha ≥ 128) are cloth. Paint into a part's BASE region or its OVERLAY
 * region — both are honored, so any skin editor works as a clothing editor:
 * socks = leg-bottom texels · crop top = upper-torso texels · gloves = hand
 * texels · a top hat = head-top texels + extrude.
 */
export type ClothingLayer = "underwear" | "inner" | "outer" | "hat";

/** Outer shell offset per layer, in fractions of one skin-texel of protrusion. */
export const LAYER_OUT: Record<ClothingLayer, number> = { underwear: 0.3, inner: 0.6, outer: 1, hat: 1 };
export const LAYER_ORDER: readonly ClothingLayer[] = ["underwear", "inner", "outer", "hat"];

export interface Garment {
  id: string;
  name: string;
  layer: ClothingLayer;
  /** Exclusivity SLOT (body role): a new garment replaces only the one in the same
   *  slot, so a bra + panties + shirt + pants coexist. Defaults to the layer name. */
  slot?: string;
  /** 64×64 skin-layout texture; opaque texels = cloth. */
  tex: THREE.Texture;
  /** Extra outward height (in texels) per atlas texel — brims, crowns, plumes. */
  extrude?: (col: number, row: number) => number;
  /** Body-AWARE shaping: given a body region's model-space dims, return an extra
   *  model-space deform (composed after the body's own) for that region — or null.
   *  A bra uses it to LIFT/gather the bust so it supports the chest instead of
   *  hanging beside it; a corset could cinch the waist, etc. */
  shape?: (dims: GarmentDims) => ((v: [number, number, number]) => [number, number, number]) | null;
}

/** Wrap a 64×64 canvas as a garment (nearest/sRGB). The canvas is the live
 *  texture — repaint it and set `tex.needsUpdate` to iterate while authoring. */
export function garmentFromCanvas(
  cv: HTMLCanvasElement, layer: ClothingLayer, id: string, name = id, extrude?: Garment["extrude"], slot?: string,
): Garment {
  return { id, name, layer, slot, tex: configure(new THREE.CanvasTexture(cv)), extrude };
}

/** Load a garment from a PNG url (64×64, skin layout). */
export function garmentFromURL(
  url: string, layer: ClothingLayer, id: string, name = id, extrude?: Garment["extrude"],
): Promise<Garment> {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (t) => resolve({ id, name, layer, tex: configure(t), extrude }),
      undefined,
      reject,
    );
  });
}
